import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getApiErrorMessage } from '@/helpers/notify'
import useTranslation from '@/hooks/useTranslation'
import useWaterSourceUsage from '../watersource/useWaterSourceUsage'
import {
  fetchFlowRuleChildren,
  findWaterSourceBlock,
  NO_FLOW_RULE_CHILDREN,
} from './flowRuleCascade'

/**
 * SCRUM-1032 — what a flow-rule cascade would remove, and whether it may.
 *
 * Shared by every confirmation that deletes a rule with its valves and
 * sensors: the pipe delete, the rule delete, and the in-pane rule delete.
 *
 * - The children are read fresh each time the confirmation opens (no cache):
 *   a list from the last opening could miss a valve added since, and what is
 *   deleted is what the user was shown.
 * - `loading` holds the Confirm button until both the children and the water
 *   sources have been read. An unread water-source list looks like "nothing
 *   uses anything", which let a used valve through.
 * - `blockReason()` is the message to show instead of deleting, or null. A
 *   failed read of the children blocks it, and so does a water source
 *   opening or metering through one of them, as the valve and sensor grids
 *   do one at a time.
 * - `childrenForDelete` is what to hand the delete: the list read, or
 *   undefined when there is none (no rule, or the read failed).
 */
const useFlowRuleCascadeCheck = (
  interfaceId: string | number | undefined,
  flowRuleId: string | number | undefined,
) => {
  const { t } = useTranslation()
  const enabled = Boolean(interfaceId) && Number(flowRuleId) > 0

  const {
    data,
    isFetching,
    isSuccess,
    error: childrenError,
  } = useQuery(
    ['flow-rule-children', String(interfaceId), String(flowRuleId)],
    () => fetchFlowRuleChildren(interfaceId!, flowRuleId!),
    { enabled, retry: 0, staleTime: 0, cacheTime: 0 },
  )
  const usage = useWaterSourceUsage(Number(interfaceId))

  // Shown in the notice. Only a successful read is handed to the delete:
  // an empty default there made the cascade skip every child, and the API
  // then refused the rule — or worse, a partial list went through.
  const children = data ?? NO_FLOW_RULE_CHILDREN
  const childrenForDelete = enabled && isSuccess && !isFetching ? data : undefined

  const loading = enabled && (isFetching || (!usage.ready && !usage.error))

  const blockReason = useCallback((): string | null => {
    if (!enabled) return null
    // What the delete would take could not be read: it does not go ahead
    // on a guess.
    if (!childrenForDelete) {
      return getApiErrorMessage(childrenError, t('somethingWentWrong'))
    }
    if (!usage.ready) {
      return getApiErrorMessage(usage.error, t('somethingWentWrong'))
    }
    const block = findWaterSourceBlock(children, usage)
    return block ? t(block.key, { names: block.names.join(', ') }) : null
  }, [enabled, usage, children, childrenForDelete, childrenError, t])

  return { children, childrenForDelete, loading, blockReason }
}

export default useFlowRuleCascadeCheck
